import { createContext, useContext, useEffect, useState } from 'react';

import type { AnySchema, TestTrack as _TestTrack } from '@betterment-oss/test-track';

export type TestTrack = _TestTrack<AnySchema>;

/**
 * This is what TestTrack was doing internally in v2. This pattern is not ideal,
 * but it makes the upgrade easier.
 *
 * After initializing TestTrack, applications should call `deferredTestTrack.resolve(testTrack)`.
 *
 * @deprecated Use `useTestTrack` instead.
 */
export const deferredTestTrack = (() => {
  let resolve!: (testTrack: TestTrack) => void;

  const promise = new Promise<TestTrack>(_resolve => {
    resolve = _resolve;
  });

  return { promise, resolve };
})();

const TestTrackContext = createContext<Promise<TestTrack>>(deferredTestTrack.promise);

/**
 * The React context defaults to the deferred promise, but can be overridden for testing purposes.
 * In the future, we should remove the default and wrap all React applications with the provider.
 */
export const TestTrackProvider = TestTrackContext.Provider;

/**
 * Obtain the Test Track instance. This hook will return null while Test Track is initializing.
 */
export function useTestTrack(): TestTrack | null {
  const contextValue = useContext(TestTrackContext);
  const [testTrack, setTestTrack] = useState<TestTrack | null>(null);

  useEffect(() => void contextValue.then(setTestTrack), [contextValue]);

  return testTrack;
}
